import { zodResolver } from '@hookform/resolvers/zod';
import { useDragAndDropEditorContext } from 'modules/dragAndDropEditor/context/DragAndDropEditorContext';
import { PropsWithChildren, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ObjectKey } from 'utils/tsHelpers';
import { z } from 'zod';
import { MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES } from './ManageCustomCategoriesModal.constants';

const ManageCustomCategoriesSchema = z.object({
  [MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES.CATEGORIES]: z.array(
    z.object({
      categoryId: z.number(),
      name: z
        .string()
        .trim()
        .min(1, 'Category name required')
        .max(40, 'Category name must not exceed 40 characters'),
    }),
  ),
});

export type ManageCustomCategoriesFormValues = Required<
  z.infer<typeof ManageCustomCategoriesSchema>
>;

export const ManageCustomCategoriesFormProvider = ({ children }: PropsWithChildren) => {
  const { messageCategories } = useDragAndDropEditorContext();

  const initialValues = useMemo(() => {
    return {
      [MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES.CATEGORIES]: messageCategories.map(
        ({ name, id: categoryId }) => ({ name, categoryId }),
      ),
    };
  }, [messageCategories]);

  const methods = useForm<ManageCustomCategoriesFormValues>({
    defaultValues: initialValues,
    mode: 'onTouched',
    resolver: zodResolver(ManageCustomCategoriesSchema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const withManageCustomCategoriesFormProvider = <Props extends Record<ObjectKey, unknown>>(
  Component: React.ComponentType<Props>,
) => {
  return (props: Props) => (
    <ManageCustomCategoriesFormProvider>
      <Component {...props} />
    </ManageCustomCategoriesFormProvider>
  );
};
